import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Filter, Search } from "lucide-react";

function Section({ label, children }) {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-800">{label}</label>
      {children}
    </div>
  );
}

function Select({ placeholder, children, value, onChange, name }) {
  return (
    <div className="relative">
      <select 
        name={name}
        value={value || ""}
        onChange={onChange}
        className="px-3 py-2 pr-8 w-full text-sm bg-white rounded-lg border border-gray-300 appearance-none outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30"
      >
        <option value="">{placeholder}</option>
        {children}
      </select>
      <span className="absolute right-3 top-1/2 text-gray-400 -translate-y-1/2 pointer-events-none">▾</span>
    </div>
  );
}

export default function AdvancedSearchDrawer({ open, onClose, onApply }) {
  const [filters, setFilters] = useState({
    coverageType: "",
    minPremium: "",
    maxPremium: "",
    duration: "",
    ageRange: "",
    claimsTime: ""
  });

  useEffect(() => {
    if (open) {
      // Store the current overflow value
      const prevOverflow = document.body.style.overflow || "";
      // Prevent body scroll when drawer is open
      document.body.style.overflow = "hidden";
      
      return () => {
        // Restore the previous overflow value when drawer closes
        document.body.style.overflow = prevOverflow;
      };
    } else {
      // Ensure overflow is restored when open is false
      document.body.style.overflow = "";
    }
  }, [open]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApply = () => {
    // Convert string values to numbers where needed and clean empty values
    const processedFilters = {};
    
    if (filters.coverageType) processedFilters.coverageType = filters.coverageType;
    if (filters.minPremium) processedFilters.minPremium = parseInt(filters.minPremium);
    if (filters.maxPremium) processedFilters.maxPremium = parseInt(filters.maxPremium);
    if (filters.duration) processedFilters.duration = parseInt(filters.duration);
    if (filters.ageRange) processedFilters.ageRange = filters.ageRange;
    if (filters.claimsTime) processedFilters.claimsTime = filters.claimsTime;
    
    onApply?.(processedFilters);
    onClose?.();
  };

  const handleClear = () => {
    setFilters({
      coverageType: "",
      minPremium: "",
      maxPremium: "",
      duration: "",
      ageRange: "",
      claimsTime: ""
    });
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[90] backdrop-blur-sm bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.aside
            className="fixed top-0 inset-y-0 left-0 z-[100] w-full max-w-md bg-white shadow-2xl flex flex-col h-full"
            initial={{ x: -420, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -420, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <div className="flex justify-between items-start p-5 pt-16 lg:pt-20 border-b border-gray-200/70 bg-white">
              <div>
                <div className="inline-flex gap-2 items-center text-emerald-600">
                  <span className="grid place-items-center w-7 h-7 bg-emerald-100 rounded-full">
                    <Filter size={16} />
                  </span>
                  <h3 className="text-lg font-bold text-gray-900">Advanced Search</h3>
                </div>
                <p className="mt-1 text-xs text-gray-500">Search, find and compare options</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-500 rounded-lg hover:bg-gray-100"
              >
                <X size={18} />
              </button>
            </div>
            <div className="overflow-y-auto overscroll-contain flex-1 p-5 space-y-5 min-h-0">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Section label="Coverage Type">
                  <Select 
                    placeholder="Select" 
                    name="coverageType"
                    value={filters.coverageType}
                    onChange={handleFilterChange}
                  >
                    <option value="medical">Medical Emergency</option>
                    <option value="accident">Accident Coverage</option>
                    <option value="road safety">Road Safety</option>
                    <option value="eye care">Eye Care</option>
                  </Select>
                </Section>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-800">Premium Range</label>
                  <div className="grid grid-cols-2 gap-3">
                    <Select 
                      placeholder="Min" 
                      name="minPremium"
                      value={filters.minPremium}
                      onChange={handleFilterChange}
                    >
                      <option value="2">₵ 2</option>
                      <option value="3">₵ 3</option>
                      <option value="5">₵ 5</option>
                      <option value="6">₵ 6</option>
                      <option value="10">₵ 10</option>
                      <option value="15">₵ 15</option>
                    </Select>
                    <Select 
                      placeholder="Max" 
                      name="maxPremium"
                      value={filters.maxPremium}
                      onChange={handleFilterChange}
                    >
                      <option value="5">₵ 5</option>
                      <option value="10">₵ 10</option>
                      <option value="15">₵ 15</option>
                      <option value="20">₵ 20</option>
                      <option value="30">₵ 30</option>
                      <option value="50">₵ 50</option>
                    </Select>
                  </div>
                </div>

                <Section label="Policy Duration">
                  <Select 
                    placeholder="Select" 
                    name="duration"
                    value={filters.duration}
                    onChange={handleFilterChange}
                  >
                    <option value="2">2 days</option>
                    <option value="3">3 days</option>
                    <option value="7">7 days</option>
                    <option value="24">24 hours</option>
                    <option value="48">48 hours</option>
                    <option value="72">72 hours</option>
                  </Select>
                </Section>

                <Section label="Age Range">
                  <Select 
                    placeholder="Select" 
                    name="ageRange"
                    value={filters.ageRange}
                    onChange={handleFilterChange}
                  >
                    <option value="18-25">18 - 25</option>
                    <option value="26-40">26 - 40</option>
                    <option value="41-60">41 - 60</option>
                  </Select>
                </Section>

                <Section label="Coverage Benefits">
                  <Select 
                    placeholder="Select" 
                    name="coverageType"
                    value={filters.coverageType}
                    onChange={handleFilterChange}
                  >
                    <option value="medical">Medical Emergency</option>
                    <option value="accident">Accident Coverage</option>
                    <option value="road safety">Road Safety Emergency</option>
                    <option value="eye care">Eye Care</option>
                  </Select>
                </Section>

                <Section label="Claims Process Time">
                  <Select 
                    placeholder="Select" 
                    name="claimsTime"
                    value={filters.claimsTime}
                    onChange={handleFilterChange}
                  >
                    <option value="instant">Within 24 hours</option>
                    <option value="1-3days">Within 32 hours</option>
                    <option value="up-to-7days">Up to 7 days</option>
                  </Select>
                </Section>
              </div>
            </div>
            <div className="sticky inset-x-0 bottom-0 p-5 bg-white border-t border-gray-200/70">
              <div className="flex gap-3">
                <button
                  onClick={handleClear}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Clear All
                </button>
                <button
                  onClick={handleApply}
                  className="flex-1 inline-flex gap-2 justify-center items-center px-5 py-3 font-semibold text-white bg-gradient-to-r from-emerald-500 to-green-600 rounded-full"
                >
                  <Search size={16} /> Apply Filters
                </button>
              </div>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

